import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

const EditInfluencer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    dob: '',
    countryId: '',
    stateId: '',
    cityId: '',
    travel_frequency: '',
    engagement_rate: '',
    portfolio_url: '',
    web_traffic: '',
    about: '',
    equipment: '',
    countryVisit: '',
    collaboration_types: '',
    image: null,
    socialMedia: {
      instagram: {
        profileUrl: '',
        followers: ''
      },
      youtube: {
        profileUrl: '',
        subscribers: ''
      }
    }
  });

  useEffect(() => {
    fetchInfluencerData();
    loadCountries();
  }, [id]);

  const fetchInfluencerData = async () => {
    try {
      const response = await apiService.get(`editInfluencer/${id}`);
      if (response && response.data && response.data.data) {
        const influencer = response.data.data;
        setFormData({
          first_name: influencer.first_name || '',
          last_name: influencer.last_name || '',
          email: influencer.email || '',
          phone: influencer.phone || '',
          gender: influencer.gender || '',
          dob: influencer.dob ? new Date(influencer.dob).toISOString().split('T')[0] : '',
          countryId: influencer.countryId?._id || '',
          stateId: influencer.stateId?._id || '',
          cityId: influencer.cityId?._id || '',
          travel_frequency: influencer.travel_frequency || '',
          engagement_rate: influencer.engagement_rate || '',
          portfolio_url: influencer.portfolio_url || '',
          web_traffic: influencer.web_traffic || '',
          about: influencer.about || '',
          equipment: influencer.equipment ? influencer.equipment.join(', ') : '',
          countryVisit: influencer.countryVisit ? influencer.countryVisit.join(', ') : '',
          collaboration_types: influencer.collaboration_types ? influencer.collaboration_types.join(', ') : '',
          image: null,
          socialMedia: {
            instagram: {
              profileUrl: influencer.socialMedia?.instagram?.profileUrl || '',
              followers: influencer.socialMedia?.instagram?.followers || ''
            },
            youtube: {
              profileUrl: influencer.socialMedia?.youtube?.profileUrl || '',
              subscribers: influencer.socialMedia?.youtube?.subscribers || ''
            }
          }
        });
        
        if (influencer.image) {
          setImagePreview(`${process.env.REACT_APP_MEDIA_URL}${influencer.image}`);
        }
      }
    } catch (error) {
      toast.error('Failed to load influencer data');
      navigate('/view-influencers');
    } finally {
      setFetchLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await apiService.get('countries');
      if (response && response.data) {
        setCountries(response.data);
      }
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadStates = async (countryId) => {
    try {
      const response = await apiService.get(`states/${countryId}`);
      if (response && response.data) {
        setStates(response.data);
      }
    } catch (error) {
      console.error('Failed to load states:', error);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const response = await apiService.get(`cities/${stateId}`);
      if (response && response.data) {
        setCities(response.data);
      }
    } catch (error) {
      console.error('Failed to load cities:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('socialMedia')) {
      const [, platform, field] = name.match(/socialMedia\[(\w+)\]\[(\w+)\]/);
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: {
            ...prev.socialMedia[platform],
            [field]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    setFormData(prev => ({ ...prev, countryId, stateId: '', cityId: '' }));
    setStates([]);
    setCities([]);
    if (countryId) {
      loadStates(countryId);
    }
  };

  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setFormData(prev => ({ ...prev, stateId, cityId: '' }));
    setCities([]);
    if (stateId) {
      loadCities(stateId);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error('File size must be 2MB or less.');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('id', id);
      
      // Add basic fields
      Object.keys(formData).forEach(key => {
        if (key !== 'image' && key !== 'socialMedia' && key !== 'equipment' && key !== 'countryVisit' && key !== 'collaboration_types') {
          submitData.append(key, formData[key] || '');
        }
      });

      // Add image if selected
      if (formData.image && formData.image instanceof File) {
        submitData.append('image', formData.image, formData.image.name);
      }

      // Add arrays
      if (formData.equipment) {
        formData.equipment.split(',').forEach(item => {
          submitData.append('equipment[]', item.trim());
        });
      }

      if (formData.countryVisit) {
        formData.countryVisit.split(',').forEach(country => {
          submitData.append('countryVisit[]', country.trim());
        });
      }

      if (formData.collaboration_types) {
        formData.collaboration_types.split(',').forEach(type => {
          submitData.append('collaboration_types[]', type.trim());
        });
      }

      // Add social media
      Object.keys(formData.socialMedia).forEach(platform => {
        Object.keys(formData.socialMedia[platform]).forEach(field => {
          submitData.append(`socialMedia[${platform}][${field}]`, formData.socialMedia[platform][field] || '');
        });
      });

      const response = await apiService.post(`updateInfluencer/${id}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Influencer profile updated successfully');
      navigate('/view-influencers');
    } catch (error) {
      toast.error('Failed to update influencer profile');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        {/* Breadcrumb */}
        <nav className="flex mb-0" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 text-xs">
            <li className="inline-flex items-center">
              <a href="/dashboard" className="text-gray-600 hover:text-[#009ef7]">Home</a>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <a href="/view-influencers" className="text-gray-600 hover:text-[#009ef7]">Influencers</a>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <span className="text-gray-500">Edit Profile</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Edit Influencer Profile</h3>
          </div>
          
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              {/* Profile Image */}
              <div className="mb-8 text-center">
                <div className="w-32 h-32 mx-auto mb-4 relative">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full border-4 border-gray-200"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center border-4 border-gray-200">
                      <i className="bi bi-person text-gray-400 text-4xl"></i>
                    </div>
                  )}
                  <label className="absolute bottom-0 right-0 bg-[#009ef7] text-white rounded-full w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-[#009ef7]/90">
                    <i className="bi bi-pencil text-xs"></i>
                    <input
                      type="file"
                      name="image"
                      onChange={handleFileChange}
                      accept=".png,.jpg,.jpeg,.webp"
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500">Allowed file types: png, jpg, jpeg, webp. Max size: 2MB</p>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                  <select
                    name="countryId"
                    value={formData.countryId}
                    onChange={handleCountryChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country._id} value={country._id}>{country.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <select
                    name="stateId"
                    value={formData.stateId}
                    onChange={handleStateChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state._id} value={state._id}>{state.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <select
                    name="cityId"
                    value={formData.cityId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city._id} value={city._id}>{city.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Professional Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Travel Frequency</label>
                  <select
                    name="travel_frequency"
                    value={formData.travel_frequency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  >
                    <option value="">Select Frequency</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Engagement Rate (%)</label>
                  <input
                    type="number"
                    name="engagement_rate"
                    value={formData.engagement_rate}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio URL</label>
                  <input
                    type="url"
                    name="portfolio_url"
                    value={formData.portfolio_url}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Web Traffic</label>
                  <input
                    type="number"
                    name="web_traffic"
                    value={formData.web_traffic}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
              </div>

              {/* About */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">About</label>
                <textarea
                  name="about"
                  value={formData.about}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              {/* Additional Fields */}
              <div className="space-y-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Equipment (comma separated)</label>
                  <input
                    type="text"
                    name="equipment"
                    value={formData.equipment}
                    onChange={handleInputChange}
                    placeholder="Camera, Drone, Laptop"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Countries Visited (comma separated)</label>
                  <input
                    type="text"
                    name="countryVisit"
                    value={formData.countryVisit}
                    onChange={handleInputChange}
                    placeholder="India, USA, UK"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Collaboration Types (comma separated)</label>
                  <input
                    type="text"
                    name="collaboration_types"
                    value={formData.collaboration_types}
                    onChange={handleInputChange}
                    placeholder="Sponsored Posts, Product Reviews"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
              </div>

              {/* Social Media */}
              <div className="mb-8">
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Social Media Information</h5>
                
                {/* Instagram */}
                <div className="border border-gray-200 rounded-lg p-4 mb-4">
                  <h6 className="font-medium text-gray-900 mb-3 flex items-center">
                    <i className="bi bi-instagram text-pink-500 mr-2"></i>
                    Instagram
                  </h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profile URL</label>
                      <input
                        type="url"
                        name="socialMedia[instagram][profileUrl]"
                        value={formData.socialMedia.instagram.profileUrl}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Followers</label>
                      <input
                        type="number"
                        name="socialMedia[instagram][followers]"
                        value={formData.socialMedia.instagram.followers}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                      />
                    </div>
                  </div>
                </div>

                {/* YouTube */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h6 className="font-medium text-gray-900 mb-3 flex items-center">
                    <i className="bi bi-youtube text-red-500 mr-2"></i>
                    YouTube
                  </h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Channel URL</label>
                      <input
                        type="url"
                        name="socialMedia[youtube][profileUrl]"
                        value={formData.socialMedia.youtube.profileUrl}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subscribers</label>
                      <input
                        type="number"
                        name="socialMedia[youtube][subscribers]"
                        value={formData.socialMedia.youtube.subscribers}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/view-influencers')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInfluencer;